import React from 'react';
import { ZoomLevel } from '@/types/timeline';

/**
 * ZoomControls props
 */
interface ZoomControlsProps {
    currentZoom: ZoomLevel;
    onZoomChange: (zoom: ZoomLevel) => void;
    /** Optional: show Today button, calls onTodayClick when pressed */
    onTodayClick?: () => void;
    /** Optional: label for scroll position indicator */
    visibleDateRange?: string;
}

const ZOOM_LEVELS = Object.values(ZoomLevel);

/**
 * ZoomControls - Button group for switching timeline zoom levels
 *
 * Story 2.7: Timeline Zoom & Scrolling
 * - AC9: 4-button group (Day, Week, Sprint, Month)
 * - AC10: Active state uses blue-600 background + white text
 * - AC12: Scroll position indicator shows visible date range
 * - AC13: Today button scrolls to current date
 * - AC8: Accessible with aria-pressed and toolbar role
 */
export const ZoomControls: React.FC<ZoomControlsProps> = ({
    currentZoom,
    onZoomChange,
    onTodayClick,
    visibleDateRange,
}) => {
    return (
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            {/* Left: zoom button group + Today */}
            <div className="flex items-center gap-2">
                <div
                    role="toolbar"
                    aria-label="Timeline zoom controls"
                    className="flex rounded-md shadow-sm border border-gray-300 overflow-hidden"
                >
                    {ZOOM_LEVELS.map((level, index) => (
                        <button
                            key={level}
                            type="button"
                            onClick={() => onZoomChange(level)}
                            aria-pressed={currentZoom === level}
                            aria-label={`Zoom to ${level} view`}
                            className={[
                                'px-3 py-1.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
                                index > 0 ? 'border-l border-gray-300' : '',
                                currentZoom === level
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 hover:bg-blue-50',
                            ].join(' ')}
                        >
                            {level}
                        </button>
                    ))}
                </div>

                {onTodayClick && (
                    <button
                        type="button"
                        onClick={onTodayClick}
                        aria-label="Scroll to today"
                        className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-blue-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 shadow-sm"
                    >
                        Today
                    </button>
                )}
            </div>

            {/* Right: scroll position indicator */}
            {visibleDateRange && (
                <div
                    aria-live="polite"
                    aria-label="Visible date range"
                    className="text-xs text-gray-600 font-medium"
                >
                    {visibleDateRange}
                </div>
            )}
        </div>
    );
};
